/**
 * Desktop view of the first quick entry. Rendered only by `../QuickEntry.tsx`, which no screen
 * renders; the live desktop view is `V2/QuickEntryDesktopV2.tsx`. Legacy: don't copy; to be
 * deleted with `../QuickEntry.tsx`.
 *
 * Differs from V2:
 * - it uses `QuantityUOM` from `@ryerson/frontend.quantity-uom-inputs` (passing `quantity` and
 *   UOM back together) instead of `V2/QuantityUOMV2.tsx`;
 * - Add Below is disabled on `!quantity` or while that input publishes `is-debouncing` true for
 *   "quick-entry", rather than on `isQuickEntryValid`, and the button is refocused when it
 *   re-enables;
 * - it passes `isSubmitted={isQuickEntrySubmitted}` to the quantity input rather than `true`;
 * - clicking Add Below disables the button at once.
 */
import React, { useState } from "react";
import { useApplication } from "@ryerson/frontend.application";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { useTheme } from "@ryerson/frontend.theme";
import { Typography } from "@ryerson/frontend.typography";
import { css } from "@emotion/react";
import { QuickEntryContent, QuickEntryText } from "./QuickEntryContent";
import { QuickEntryProps } from "./QuickEntry.models";
import { Icon } from "@ryerson/frontend.assets";
import { QuantityUOM } from "@ryerson/frontend.quantity-uom-inputs";
import { UOMValue } from "@ryerson/common.uom";
import { SelectKeys } from "./QuickEntry.models";
import {
	ErrorMessage,
	ErrorContainer,
	QuickEntryContainer,
	LabelContainer,
} from "./StyledComponents";

import { SearchInput, ProductList, AddBelowButton, PriceUOM } from "./SharedComponents";

/** V1 desktop quick-entry row; not rendered by any screen. */
const QuickEntryDesktop: React.FC<QuickEntryProps> = ({
	searchInputContainerRef,
	showDropdown,
	setShowDropdown,
	searchResult,
	onClickSelectItem,
	isError,
	quantity,
	setQuantity,
	qtyUOM,
	priceUOM,
	priceUOMDropdown,
	onClickAddBelow,
	displaySearchTerm,
	onChangeText,
	searchTerm,
	setPriceUOM,
	closeQuickEntry,
	selectedProduct,
	itemAddSuccess,
	itemAddFail,
	updateQuantityUom,
	isQuickEntrySubmitted,
	handleValidationResponse,
	hasMounted,
	stateMessenger,
	qtyUOMDropdown,
}) => {
	const { theme } = useTheme();
	const backgroundPrimary = theme.colors.primary.background;
	const headerSecondary = theme.colors.secondary.header;
	const textSecondary = theme.colors.secondary.text;
	const { red } = theme.colors.secondary;
	const [tempItemNumber, setTempItemNumber] = useState<any>();
	/*pubsub messaging*/
	const [message, setMessage] = React.useState<Array<any>>([]);
	// to work with arrow keys
	const [productSelectedIndex, setProductSelectedIndex] = React.useState<number>(-1);

	// Publish call to pubsub
	const publishMessage = (messageString: string, data: any, topic: any) => {
		/* note the use of setTimeout is required for publishing messages*/
		setTimeout(() => stateMessenger?.publish(messageString, data, topic), 10);
	};

	const [isDebouncing, setIsDebouncing] = React.useState<boolean>(false);
	const [buttonDisabled, setIsButtonDisabled] = React.useState<boolean>(false);
	// create a useRef to tack when to cycle focus if using keys
	const resetFocus = React.useRef<boolean>(false);
	const isButtonFocused = React.useRef<boolean>(false);

	//Note:  this name utilized in keydown even code
	const buttonClassName = "button-container-add-below";

	React.useEffect(() => {
		if (!quantity || isDebouncing) {
			setIsButtonDisabled(true);
		} else {
			if (isButtonFocused.current) {
				setTimeout(() => {
					(
						document.querySelector(`.${buttonClassName}  div[role='button']`) as any
					).focus();
					isButtonFocused.current = false;
				}, 20);
			}
			setIsButtonDisabled(false);
		}
	}, [quantity, isDebouncing]);

	const {
		localization: { language },
	} = useApplication();

	const content: QuickEntryText = QuickEntryContent[language] || QuickEntryContent.en;

	React.useEffect(() => {
		// Subscribe to pub-sub  stateMessenger
		stateMessenger?.subscribe((msg: string, value: any, topic: any) =>
			setMessage([msg, value, topic])
		);

		if (hasMounted) {
			const _query = document && (document as any).querySelector.bind(document);
			const elInput = _query("[data-testid=quick-entry-search-input]");
			elInput?.focus();
		}
	}, [hasMounted]);

	/** @desc -Listener for Pub Sub Messages from other components 	*/
	/* Note: the use of publishing messages (pubsub) to receive messages from quantityUOm Component
			if value is true  of "is-debouncing" then we are currently debouncing 
	*/
	React.useEffect(() => {
		let [messageString, value, id] = message;
		if (messageString === "is-debouncing" && id === "quick-entry") {
			setIsDebouncing(value);
		}
	}, [message]);

	// Note: Utilized for scrolling the product list with arrow keys
	const setScrollTop = (amount: number) => {
		const UList = document.querySelector("ul.product-list");
		UList && (UList.scrollTop += amount);
	};

	// Note: used when using tab keys to navigate across the quick entry and loop
	// back to the search input
	const handleFocusChanged = (e: React.FocusEventHandler<HTMLDivElement> | undefined | any) => {
		if (e.target?.className?.includes(buttonClassName)) {
			isButtonFocused.current = true;
			resetFocus.current = true;
		} else {
			if (e.target?.id === "quantity-uom-input-quick-entry") {
				setShowDropdown(false);
			}
			isButtonFocused.current = false;
			resetFocus.current = false;
		}
	};
	//  Works with handleFocusChanged to set the focus on search input (loops around)
	const focusGuard = (e: React.KeyboardEventHandler<HTMLDivElement> | undefined | any) => {
		if (e.key === "Tab" && resetFocus.current === true) {
			resetFocus.current = false;
			const elInput = document?.querySelector("#quick-entry-search-input");
			setTimeout(() => {
				elInput && (elInput as any)?.blur();
				elInput && (elInput as any)?.focus();
			}, 20);
		}
	};

	/**
	 *
	 * @desc - Function to handle arrow keys for product list
	 * @param e - event
	 */
	const handleArrowKeys = (e: React.KeyboardEventHandler<HTMLDivElement> | undefined | any) => {
		if (showDropdown) {
			if (e.key === SelectKeys.ArrowDown) {
				setProductSelectedIndex((prev) => Math.min(searchResult?.length - 1, prev + 1));
				if (productSelectedIndex > 0 && productSelectedIndex < searchResult.length - 2) {
					setScrollTop(120);
				}
			} else if (e.key === SelectKeys.ArrowUp) {
				setProductSelectedIndex((prev) => Math.max(0, prev - 1));
				if (productSelectedIndex < searchResult.length - 2) {
					setScrollTop(-120);
				}
			} else if (e.key === SelectKeys.Enter) {
				const selectedProduct = searchResult[productSelectedIndex];
				if (selectedProduct) {
					onClickSelectItem(selectedProduct);
				}
			}
		}
	};
	React.useEffect(() => {
		if (productSelectedIndex >= 0) {
			setProductSelectedIndex(-1);
		}
	}, [showDropdown]);

	return (
		<>
			<div onKeyDown={focusGuard} onFocus={handleFocusChanged}>
				<QuickEntryContainer {...{ background: backgroundPrimary }}>
					<Flex alignItems="flex-start" css={{ width: "85%", padding: "17px 0 33px" }}>
						<FlexItem css={{ width: "35%" }}>
							<Flex
								itemRef={searchInputContainerRef}
								direction="column"
								style={css`
									padding-right: 10px;
								`}
							>
								<div onKeyDown={handleArrowKeys}>
									<SearchInput
										inputId="quick-entry-search-input"
										displaySearchTerm={displaySearchTerm}
										handleChange={(e) => onChangeText(e)}
										handleFocus={() => setShowDropdown(true)}
									/>
								</div>

								{showDropdown && (
									<ProductList
										searchResults={searchResult}
										handleClick={onClickSelectItem}
										selectItemIndex={productSelectedIndex}
									/>
								)}
								{isError && (
									<ErrorContainer>
										<ErrorMessage
											{...{
												bgColor: backgroundPrimary,
												bColor: textSecondary,
											}}
										>
											{"No Results Found".replace(
												"%searchString%",
												searchTerm.toString()
											)}
										</ErrorMessage>
									</ErrorContainer>
								)}
							</Flex>
						</FlexItem>
						<FlexItem>
							<Flex
								direction="column"
								style={css`
									padding-right: 10px;
								`}
							>
								<LabelContainer>
									<Typography color={headerSecondary}>
										{content.quantityUom}
									</Typography>
									<Typography color={red}> *</Typography>
								</LabelContainer>

								<QuantityUOM
									{...{
										id: "quick-entry",
										itemData: selectedProduct || {},
										quantityUomOptions: qtyUOMDropdown,
										size: "sm",
										type: "primary",
										disabled: !selectedProduct,
										dataTestId: "quantity-uom-test",
										isFocused: !!selectedProduct,
										popperDisplayTime: 2000,
									}}
									isSubmitted={isQuickEntrySubmitted}
									quantityValue={quantity}
									uomValue={qtyUOM as UOMValue}
									handleChange={(quantityValue, uomValue) => {
										setQuantity(quantityValue);
										updateQuantityUom(uomValue);
									}}
									handleValidationResponse={handleValidationResponse}
									stateMessenger={stateMessenger}
								/>
							</Flex>
						</FlexItem>

						<div
							css={{ width: "150px" }}
							onMouseLeave={() =>
								publishMessage(`to-debounce`, "mouseout", "quick-entry")
							}
							onFocus={() => publishMessage(`to-debounce`, "focus", "quick-entry")}
							onChange={(value) =>
								publishMessage(`to-debounce`, "change", "quick-entry")
							}
						>
							<PriceUOM
								handleChange={setPriceUOM}
								isDisabled={() => {
									return !selectedProduct || priceUOMDropdown.length === 1;
								}}
								priceUOM={priceUOM}
								priceUOMDropdown={priceUOMDropdown}
							/>
						</div>
						<FlexItem css={{ width: "130px" }}>
							<AddBelowButton
								buttonClassName={buttonClassName}
								itemNumber={tempItemNumber}
								itemAddSuccess={itemAddSuccess}
								itemAddFail={itemAddFail}
								handleClick={() => {
									setIsButtonDisabled(true);
									setTempItemNumber(selectedProduct?.partNumber);
									onClickAddBelow(() => {
										resetFocus.current = false;
										const elInput = document?.querySelector(
											"#quick-entry-search-input"
										);
										(elInput as any)?.focus();
									});
								}}
								isDisabled={buttonDisabled}
							/>
						</FlexItem>
					</Flex>
					<Flex
						css={{
							width: "15%",
							height: "100%",
							paddingTop: "15px",
							paddingRight: "18px",
						}}
						justifyContent="flex-end"
						alignItems="flex-start"
					>
						<Icon
							onClick={closeQuickEntry}
							icon="close"
							color={textSecondary}
							circled={true}
							dataTestId="shopping-list-details-quick-entry-close"
						/>
					</Flex>
				</QuickEntryContainer>
			</div>
		</>
	);
};

export default QuickEntryDesktop;
